import type { AdComponent } from '../../../../../core/data-sources/content-components/types.js';
import type { AdInsertionRule, AdContext } from '../../../../types/ad-types.js';
import type {
  CountableItem,
  InsertAtIntervalOptions,
  Insertion,
  ListSlot,
  PositionedListSlot,
  Slot,
} from './types.js';

import {
  Action,
  AdContexts,
  AdCssClass,
  BRANDBOARD_MARKER,
  ContentType,
  Device,
  NETBOARD_MARKER,
  ReservedSlotId,
} from '../../../../types/ad-types.js';
import {
  adInsertionRules,
  getSlotPool,
  getListPlacements,
} from '../../../../ads-config/placements.js';

const isAdComponent = <Item extends CountableItem>(
  item: Item | AdComponent | undefined,
): item is AdComponent => item?.type === ContentType.AD;

const hasIntention =
  <Item extends CountableItem>(intention: string) =>
  (item: Item): boolean =>
    item.presentationProperties?.intention === intention;

const isPositionedSlot = (slot: ListSlot): slot is PositionedListSlot =>
  slot.position !== null;

const insertAdAtIndex = <Item extends CountableItem>(
  ad: AdComponent,
  insertAt: number,
  items: Array<Item | AdComponent>,
): Array<Item | AdComponent> => {
  const lastItemIsAd = isAdComponent(items.at(-1));
  const wouldStackOnExistingTrailingAd =
    insertAt >= items.length && lastItemIsAd;

  if (wouldStackOnExistingTrailingAd) {
    return items;
  }

  return [...items.slice(0, insertAt), ad, ...items.slice(insertAt)];
};

const removeTrailingAd = <Item extends CountableItem>(
  items: Array<Item | AdComponent>,
): Array<Item | AdComponent> => {
  if (items.length === 0 || !isAdComponent(items.at(-1))) {
    return items;
  }

  return items.slice(0, -1);
};

const applyInsertions = <Item extends CountableItem>(
  items: Array<Item>,
  insertions: Array<Insertion>,
): Array<Item | AdComponent> => {
  let result: Array<Item | AdComponent> = [...items];

  for (const { atIndex, placement, slotIndex } of [...insertions].reverse()) {
    const ad: AdComponent = {
      id: placement.targetId,
      type: ContentType.AD,
      cssClass: placement.cssClass,
      sizes: placement.sizes,
      position: slotIndex,
    };
    result = insertAdAtIndex(ad, atIndex, result);
  }

  return result;
};

const buildShouldCount = <Item extends CountableItem>(
  rule: AdInsertionRule,
): ((item: Item) => boolean) => {
  if ('countAction' in rule) {
    const { countAction } = rule;

    return (item: Item): boolean =>
      item.presentationProperties?.intention === countAction;
  }

  if ('countExcept' in rule) {
    const excludedTypes = new Set(rule.countExcept);

    return (item: Item): boolean => {
      const { type } = item;

      if (typeof type !== 'string' || type === '') {
        return false;
      }

      return !excludedTypes.has(type) && type !== ContentType.AD;
    };
  }

  const includedTypes = new Set(rule.count);

  return (item: Item): boolean => {
    const { type, fullWidthTeaser } = item;
    const isFullWidthTeaser =
      includedTypes.has(ContentType.FULL_WIDTH_TEASER) &&
      type === ContentType.TEASER &&
      fullWidthTeaser === true;

    if (isFullWidthTeaser) {
      return true;
    }

    return includedTypes.has(type ?? '');
  };
};

const insertAdsAtInterval = <Item extends CountableItem>(
  items: Array<Item>,
  options: InsertAtIntervalOptions<Item>,
): Array<Item | AdComponent> => {
  const { shouldCount, interval, anchorBefore, placements } = options;

  if (interval <= 0) {
    return removeTrailingAd([...items]);
  }

  let count = 0;
  let nextPlacementIndex = 0;
  const insertions: Array<Insertion> = [];

  for (const [index, item] of items.entries()) {
    if (!shouldCount(item)) {
      continue;
    }

    count++;

    const placementsExhausted = nextPlacementIndex >= placements.length;

    if (count % interval !== 0 || placementsExhausted) {
      continue;
    }

    insertions.push({
      atIndex: anchorBefore ? index : index + 1,
      placement: placements[nextPlacementIndex],
      slotIndex: nextPlacementIndex,
    });
    nextPlacementIndex++;
  }

  return removeTrailingAd(applyInsertions(items, insertions));
};

const cssClassForContext = (context: AdContext): string => {
  const isFrontpageStyled =
    context === AdContexts.FRONTPAGE ||
    context === AdContexts.SECTION ||
    context === AdContexts.LIST;

  return isFrontpageStyled ? AdCssClass.FRONTPAGE : AdCssClass.ARTICLE;
};

const buildGuideShouldCount = <Item extends CountableItem>(
  rule: AdInsertionRule,
): ((item: Item) => boolean) => {
  const isGroupStart = hasIntention<Item>(Action.GROUP_START);
  const isGroupEnd = hasIntention<Item>(Action.GROUP_END);
  const countsOutsideGroup = buildShouldCount<Item>(rule);
  let insideGroup = false;

  return (item: Item): boolean => {
    if (isGroupStart(item)) {
      insideGroup = true;

      return false;
    }

    if (isGroupEnd(item)) {
      insideGroup = false;

      return true;
    }

    if (insideGroup) {
      return false;
    }

    return countsOutsideGroup(item);
  };
};

const insertAdsAtListPositions = <Item extends CountableItem>(
  pageElements: Array<Item>,
  device: string,
  cssClass: string,
): Array<Item | AdComponent> => {
  const positionedSlots = getListPlacements(device)
    .filter(isPositionedSlot)
    .filter(({ position }) => position < pageElements.length)
    .sort((a, b) => a.position - b.position);

  let result: Array<Item | AdComponent> = [...pageElements];

  for (const { targetId, sizes, position } of positionedSlots) {
    const ad: AdComponent = {
      id: targetId,
      type: ContentType.AD,
      cssClass,
      sizes,
      position,
    };
    result = insertAdAtIndex(ad, position, result);
  }

  return removeTrailingAd(result);
};

const insertAdsPlacements = <Item extends CountableItem>(
  isMobile: boolean,
  pageElements: Array<Item>,
  context: AdContext,
): Array<Item | AdComponent> => {
  const device = isMobile ? Device.MOBILE : Device.DESKTOP;
  const cssClass = cssClassForContext(context);

  if (context === AdContexts.LIST) {
    return insertAdsAtListPositions(pageElements, device, cssClass);
  }

  const rule = adInsertionRules[context];
  const slotPool = getSlotPool(rule.pageType, device);
  const slots: Array<Slot> = slotPool
    .filter(
      ({ targetId }) =>
        targetId !== ReservedSlotId.TAKEOVER &&
        targetId !== ReservedSlotId.SPONSORSTRIPE,
    )
    .map(({ targetId, sizes }) => ({ targetId, sizes, cssClass }));

  if (context === AdContexts.FRONTPAGE) {
    const frontpageSlots = slots.filter(
      ({ targetId }) => !targetId?.includes(NETBOARD_MARKER),
    );

    return insertAdsAtInterval(pageElements, {
      shouldCount: buildShouldCount(rule),
      interval: rule.interval,
      anchorBefore: rule.anchorBefore,
      placements: frontpageSlots,
    });
  }

  const articleSlots = slots.filter(
    ({ targetId }) => !targetId?.includes(BRANDBOARD_MARKER),
  );
  const shouldCount =
    context === AdContexts.GUIDE
      ? buildGuideShouldCount<Item>(rule)
      : buildShouldCount<Item>(rule);

  return insertAdsAtInterval(pageElements, {
    shouldCount,
    interval: rule.interval,
    anchorBefore: rule.anchorBefore,
    placements: articleSlots,
  });
};

export { isAdComponent, insertAdsAtInterval, insertAdsPlacements };
